import { useCallback, useRef, type KeyboardEvent } from "react";
import type { KeywordSearchConfig } from "@/features/chat/utils/keywordSearchConfigs";

type SearchMode = "keyword" | "participants";
type SearchRoomType = "user_dm" | "user_group";

interface EntrySearchHandler {
  searchChatEntries: (
    keyword: string,
    type: SearchRoomType,
  ) => Promise<unknown> | unknown;
}

interface UseChatSearchExecutionOptions {
  keyword: string;
  searchMode: SearchMode;
  hasParticipants: boolean;
  isCheckingExisting: boolean;
  keywordSearchConfigs: KeywordSearchConfig[];
  dmEntrySearch: EntrySearchHandler;
  topicEntrySearch: EntrySearchHandler;
  onSearchPerformed: () => void;
}

export const useChatSearchExecution = ({
  keyword,
  searchMode,
  hasParticipants,
  isCheckingExisting,
  keywordSearchConfigs,
  dmEntrySearch,
  topicEntrySearch,
  onSearchPerformed,
}: UseChatSearchExecutionOptions) => {
  const isSearching = useRef(false);

  const performSearch = useCallback(async () => {
    const searchTerm = keyword.trim();
    if (isSearching.current) return;

    if (searchMode === "keyword" && !searchTerm) return;
    if (searchMode === "participants" && !hasParticipants) return;
    if (searchMode === "participants" && isCheckingExisting) return;

    isSearching.current = true;
    onSearchPerformed();

    try {
      if (searchMode === "keyword") {
        await Promise.all(
          keywordSearchConfigs.map((config) =>
            config.search(searchTerm, config.type),
          ),
        );
        await Promise.all([
          dmEntrySearch.searchChatEntries(searchTerm, "user_dm"),
          topicEntrySearch.searchChatEntries(searchTerm, "user_group"),
        ]);
      }
    } catch (error) {
      console.error("検索失敗:", error);
    } finally {
      isSearching.current = false;
    }
  }, [
    dmEntrySearch,
    hasParticipants,
    isCheckingExisting,
    keyword,
    keywordSearchConfigs,
    onSearchPerformed,
    searchMode,
    topicEntrySearch,
  ]);

  const handleSearch = useCallback(() => {
    void performSearch();
  }, [performSearch]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        void performSearch();
      }
    },
    [performSearch],
  );

  return {
    isSearching,
    performSearch,
    handleSearch,
    handleKeyDown,
  };
};
